import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ModalConfirmLogout from "../../components/ModalConfirmLogout";
import Header from "../../components/Header";
import axios from "axios";
import Cookies from "js-cookie";
import Swal from "sweetalert2";

const TableGameNew = () => {
  const navigate = useNavigate();
  const userInfo = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user_info") || "null");
    } catch {
      return null;
    }
  }, []);

  const [userCoins, setUserCoins] = useState<number>(userInfo?.coins ?? 0);
  const [isShowLogout, setIsShowLogout] = useState(false);
  const gameImg: string = (localStorage.getItem("title_img") || "/assets/phantichchitiet.gif") as string;
  const [gameTitle, setGameTitle] = useState<string>("THỢ SĂN CÁ MẬP");
  const winPercent = parseInt(localStorage.getItem("win_percent") || "90");

  const [points, setPoints] = useState<string>("");
  const [isSpinning, setIsSpinning] = useState(false);
  const [manualValues, setManualValues] = useState({ rounds: 0, minBet: "0" });
  const [autoValues, setAutoValues] = useState({ rounds: 0, minBet: "0" });
  const [timeSlotText, setTimeSlotText] = useState(">Chưa có dữ liệu");

  useEffect(() => {
    const raw = localStorage.getItem("user_info");
    if (raw) {
      try {
        setUserCoins(JSON.parse(raw)?.coins ?? 0);
      } catch {}
    }
  }, []);

  useEffect(() => {
    const titleFromStorage = localStorage.getItem("title_text");
    if (titleFromStorage) setGameTitle(titleFromStorage);
  }, []);

  useEffect(() => {
    Swal.fire({
      html: `<div style="padding: 18px; color:white; font-family: 'Courier New', monospace;">Đang tải dữ liệu...</div>`,
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      timer: 1200,
      background: "rgba(0,0,0,0.85)",
    });
  }, []);

  const deductCoins = async (amount: number = 1, action: string = "PLAY_GAME") => {
    try {
      const token = Cookies.get("access_token");
      if (!token) return true; // không chặn luồng khi dev chưa login
      const res = await axios.post(
        `${process.env.REACT_APP_URL_API}/users/subtract-coins-for-action`,
        { amount, action },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      const newCoins = res.data?.coins ?? userCoins;
      setUserCoins(newCoins);
      const raw = localStorage.getItem("user_info");
      if (raw) {
        const u = JSON.parse(raw); u.coins = newCoins; localStorage.setItem("user_info", JSON.stringify(u));
      }
      return true;
    } catch (e) {
      return false;
    }
  };

  const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
  const randomInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
  const jitter = (value: number, percent: number) => {
    const spread = value * percent;
    return value + (Math.random() * 2 - 1) * spread;
  };

  // Gợi ý cho nổ hũ: quay mồi ngắn + min thấp, auto dài hơn + min vừa phải.
  // Tránh scale tuyến tính theo điểm nhập để không ra thông số quá đà.
  const generateSpinSuggestions = (target: number, coins: number) => {
    const safeTarget = Math.max(target, 10);
    const bankroll = Math.max(coins, safeTarget * 8, 200);
    const targetPressure = safeTarget / bankroll;

    // Nếu mục tiêu cao hơn sức vốn => ép về cấu hình bảo toàn vốn.
    const isConservative = targetPressure > 0.2;

    let manualRoundsRange: [number, number];
    let autoRoundsRange: [number, number];
    let manualRatio: number;
    let autoRatio: number;

    if (safeTarget <= 300) {
      manualRoundsRange = [8, 16];
      autoRoundsRange = [24, 42];
      manualRatio = 0.018;
      autoRatio = 0.028;
    } else if (safeTarget <= 1500) {
      manualRoundsRange = [12, 20];
      autoRoundsRange = [32, 56];
      manualRatio = 0.014;
      autoRatio = 0.022;
    } else if (safeTarget <= 5000) {
      manualRoundsRange = [16, 26];
      autoRoundsRange = [42, 72];
      manualRatio = 0.011;
      autoRatio = 0.018;
    } else {
      manualRoundsRange = [20, 32];
      autoRoundsRange = [52, 90];
      manualRatio = 0.009;
      autoRatio = 0.015;
    }

    if (isConservative) {
      manualRoundsRange = [manualRoundsRange[0] + 2, manualRoundsRange[1] + 4];
      autoRoundsRange = [autoRoundsRange[0] + 6, autoRoundsRange[1] + 8];
      manualRatio *= 0.82;
      autoRatio *= 0.86;
    }

    const manualRounds = randomInt(manualRoundsRange[0], manualRoundsRange[1]);
    const autoRounds = randomInt(autoRoundsRange[0], autoRoundsRange[1]);

    const manualMinRaw = jitter(safeTarget * manualRatio, 0.15);
    const autoMinRaw = jitter(safeTarget * autoRatio, 0.18);

    // Giới hạn mức min theo vốn để không bị "đuối xu" quá nhanh.
    const manualMin = Math.floor(clamp(manualMinRaw, 1, bankroll * 0.03));
    const autoMin = Math.floor(clamp(Math.max(autoMinRaw, manualMin * 1.15), 1, bankroll * 0.06));

    return {
      manual: { rounds: manualRounds, min: manualMin },
      auto: { rounds: autoRounds, min: autoMin },
    };
  };

  const formatHourMinute = (date: Date) => {
    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  };

  const buildTimeSlotText = (manualRounds: number, autoRounds: number) => {
    const now = new Date();
    const startDelay = randomInt(1, 4); // bắt đầu sau 1-4 phút
    const strategyIsAuto = autoRounds >= manualRounds + 10;
    const duration = strategyIsAuto ? randomInt(18, 25) : randomInt(12, 18);

    const start = new Date(now.getTime() + startDelay * 60 * 1000);
    const end = new Date(start.getTime() + duration * 60 * 1000);
    const strategy = strategyIsAuto ? "ưu tiên auto" : "ưu tiên mồi";

    return `>${formatHourMinute(start)} - ${formatHourMinute(end)} (${strategy})`;
  };

  // Actions
  const handleStart = async () => {
    const inputRaw = points.trim();
    if (!inputRaw) {
      Swal.fire({
        icon: "warning",
        title: "Thiếu dữ liệu",
        text: "Vui lòng nhập điểm trước khi bấm BẮT ĐẦU PHÂN TÍCH.",
        confirmButtonText: "Đã hiểu",
      });
      return;
    }

    const input = parseInt(inputRaw);
    if (!input || input <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Điểm chưa hợp lệ",
        text: "Điểm phải lớn hơn 0.",
        confirmButtonText: "Đã hiểu",
      });
      return;
    }
    
    // Check if user has enough coins
    if (userCoins < 1) {
      showInsufficientXuModal();
      return;
    }

    // Deduct 1 xu
    const ok = await deductCoins(1, "SPIN_START");
    if (!ok) {
      showInsufficientXuModal();
      return;
    }
    
    setIsSpinning(true);
    
    const suggestion = generateSpinSuggestions(input, userCoins);
    
    const manualBetFormatted = suggestion.manual.min >= 1000
      ? `${Math.floor(suggestion.manual.min / 1000)}K`
      : `${suggestion.manual.min}`;
    
    const autoBetFormatted = suggestion.auto.min >= 1000
      ? `${Math.floor(suggestion.auto.min / 1000)}K`
      : `${suggestion.auto.min}`;
    
    setManualValues({ 
      rounds: suggestion.manual.rounds, 
      minBet: manualBetFormatted 
    });
    setAutoValues({ 
      rounds: suggestion.auto.rounds, 
      minBet: autoBetFormatted 
    });
    setTimeSlotText(buildTimeSlotText(suggestion.manual.rounds, suggestion.auto.rounds));
    setTimeout(() => setIsSpinning(false), 250);
  };

  const showInsufficientXuModal = () => {
    // Create modal element
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.9);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10000;
      backdrop-filter: blur(8px);
      animation: modalFadeIn 0.3s ease-out;
    `;

    const modalContent = document.createElement('div');
    modalContent.style.cssText = `
      background: 
        radial-gradient(circle at 30% 20%, rgba(255, 107, 107, 0.1) 0%, transparent 50%),
        radial-gradient(circle at 70% 80%, rgba(255, 235, 59, 0.05) 0%, transparent 50%),
        linear-gradient(135deg, 
          rgba(26, 26, 46, 0.95) 0%, 
          rgba(22, 33, 62, 0.98) 25%,
          rgba(15, 52, 96, 0.95) 50%,
          rgba(22, 33, 62, 0.98) 75%,
          rgba(26, 26, 46, 0.95) 100%);
      border: 2px solid #ff6b6b;
      border-radius: 25px;
      padding: 35px;
      text-align: center;
      color: white;
      max-width: 420px;
      width: 90%;
      box-shadow: 
        0 0 40px rgba(255, 107, 107, 0.4),
        0 0 80px rgba(255, 235, 59, 0.2),
        0 20px 60px rgba(0, 0, 0, 0.5),
        inset 0 0 30px rgba(255, 107, 107, 0.1);
      position: relative;
      animation: modalSlideIn 0.4s ease-out;
      font-family: 'Courier New', monospace;
    `;

    modalContent.innerHTML = `
      <style>
        @keyframes modalFadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes modalSlideIn {
          0% { 
            opacity: 0;
            transform: translateY(-30px) scale(0.9);
            filter: blur(5px);
          }
          100% { 
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0);
          }
        }
        @keyframes iconPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        @keyframes textGlow {
          0%, 100% { text-shadow: 0 0 10px rgba(255, 107, 107, 0.5); }
          50% { text-shadow: 0 0 20px rgba(255, 107, 107, 0.8); }
        }
      </style>
      
      <div style="display: flex; align-items: center; margin-bottom: 25px; animation: textGlow 2s ease-in-out infinite;">
        <div style="
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #ffeb3b, #ffc107);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 20px;
          font-size: 28px;
          color: #000;
          font-weight: bold;
          box-shadow: 
            0 0 20px rgba(255, 235, 59, 0.6),
            0 0 40px rgba(255, 193, 7, 0.3);
          animation: iconPulse 1.5s ease-in-out infinite;
          border: 2px solid rgba(255, 255, 255, 0.3);
        ">!</div>
        <h2 style="
          margin: 0; 
          color: #ff6b6b; 
          font-size: 26px; 
          font-weight: 900;
          text-shadow: 
            0 0 15px rgba(255, 107, 107, 0.8),
            0 0 25px rgba(255, 107, 107, 0.5);
          letter-spacing: 2px;
          text-transform: uppercase;
        ">KHÔNG ĐỦ XU!</h2>
      </div>
      
      <div style="margin-bottom: 20px; font-size: 16px;">
        <div style="
          margin-bottom: 12px; 
          padding: 8px 12px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        ">
          <span style="color: rgba(255, 255, 255, 0.9); font-weight: 500;">SỐ XU HIỆN TẠI: </span>
          <span style="
            color: #ffeb3b; 
            font-weight: bold; 
            font-size: 20px;
            text-shadow: 0 0 10px rgba(255, 235, 59, 0.6);
          ">${userCoins} XU</span>
        </div>
        <div style="
          margin-bottom: 12px; 
          padding: 8px 12px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        ">
          <span style="color: rgba(255, 255, 255, 0.9); font-weight: 500;">YÊU CẦU TỐI THIỂU: </span>
          <span style="
            color: #ffeb3b; 
            font-weight: bold; 
            font-size: 20px;
            text-shadow: 0 0 10px rgba(255, 235, 59, 0.6);
          ">1 XU</span>
        </div>
      </div>
      
      <div onclick="window.open('https://t.me/congnghemoi668', '_blank')" style="
        margin-bottom: 25px; 
        font-size: 14px; 
        color: #64b5f6;
        padding: 10px 15px;
        background: rgba(100, 181, 246, 0.1);
        border-radius: 4px;
        border: 1px solid rgba(100, 181, 246, 0.3);
        text-shadow: 0 0 10px rgba(100, 181, 246, 0.5);
        letter-spacing: 1px;
        cursor: pointer;
        transition: all 0.3s ease;
      " onmouseover="
        this.style.backgroundColor='rgba(100, 181, 246, 0.2)';
        this.style.borderColor='rgba(100, 181, 246, 0.5)';
        this.style.transform='scale(1.02)';
      " onmouseout="
        this.style.backgroundColor='rgba(100, 181, 246, 0.1)';
        this.style.borderColor='rgba(100, 181, 246, 0.3)';
        this.style.transform='scale(1)';
      ">
        » LIÊN HỆ ADMIN ĐỂ NẠP XU «
      </div>
      
      <button onclick="this.parentElement.parentElement.remove()" style="
        background: linear-gradient(45deg, #ff6b6b, #ee5a24);
        border: none;
        border-radius: 12px;
        padding: 14px 35px;
        color: white;
        font-size: 16px;
        font-weight: bold;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 
          0 0 20px rgba(255, 107, 107, 0.4),
          0 4px 15px rgba(0, 0, 0, 0.3);
        text-transform: uppercase;
        letter-spacing: 1px;
        font-family: 'Courier New', monospace;
      " onmouseover="
        this.style.transform='translateY(-2px)';
        this.style.boxShadow='0 0 30px rgba(255, 107, 107, 0.6), 0 6px 20px rgba(0, 0, 0, 0.4)';
      " onmouseout="
        this.style.transform='translateY(0)';
        this.style.boxShadow='0 0 20px rgba(255, 107, 107, 0.4), 0 4px 15px rgba(0, 0, 0, 0.3)';
      ">
        ĐÓNG
      </button>
    `;

    modal.appendChild(modalContent);
    document.body.appendChild(modal);
  };

  return (
    <div className="min-h-screen w-full bg-repeat bg-top" style={{ position: "relative" }}>
      {/* Video background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: -1,
          backgroundSize: "contain",
          backgroundRepeat: "repeat",
          backgroundPosition: "top"
        }}
      >
        <source
          src={window.innerWidth <= 430 ? "/assets/bg-mb.mov" : "/assets/bg-pc.mov"}
          type="video/mp4"
        />
      </video>

      <Header setIsShowLogout={() => setIsShowLogout(true)} />

      {/* Khung chính duy nhất */}
      <div style={{ width: "100%", display: "flex", justifyContent: "center", paddingBottom: 16, marginTop: "5rem" }}>
        <div
          style={{
            position: "relative",
            width: 475,
            height: 755,
            maxWidth: "calc(100vw - 10px)",
            backgroundImage: "url('/assets/bg-result.png')",
            backgroundSize: "100% 100%",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            padding: "14px 24px",
            color: "#fff",
            boxSizing: "border-box",
          }}
        >
          {/* Nút quay lại nằm cạnh trái modal */}
          <button
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/NH"))}
            aria-label="Quay lại"
            style={{
              position: "absolute",
              left: window.innerWidth <= 640 ? 0 : -98,
              top: window.innerWidth <= 640 ? -40 : -44,
              transform: "none",
              border: "none",
              background: "transparent",
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: 0,
              color: "#fff",
              fontWeight: 700,
              fontSize: 18,
              zIndex: 5,
              cursor: "pointer",
            }}
          >
            <img src="/assets/arrow-icon.png" alt="arrow" style={{ width: 28, height: 28 }} />
            <span>Quay lại</span>
          </button>

          {/* Div 1: robot */}
          <div style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: 8 }}>
            <img
              src="/assets/robot.gif"
              alt="robot"
              style={{ width: 430, height: 255, maxWidth: "100%", objectFit: "contain" }}
            />
          </div>

          {/* Div 2: game image + title/loading/% */}
          <div style={{ display: "flex", gap: 12, marginTop: -4, alignItems: "center" }}>
            <img
              src={gameImg}
              alt="game"
              onError={(e) => ((e.currentTarget as HTMLImageElement).src = "/assets/nohu.gif")}
              style={{ width: 132, height: 132, borderRadius: 12, objectFit: "cover", border: "2px solid #ff3b30" }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "28.17px",
                  fontWeight: 900,
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  marginBottom: 6,
                  textShadow: "0 0 10px rgba(0,0,0,0.7)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={gameTitle}
              >
                {gameTitle}
              </div>
              <div style={{ position: "relative", borderRadius: 4, overflow: "hidden", height: 86 }}>
                <video
                  src="/assets/loading.webm"
                  autoPlay
                  loop
                  muted
                  playsInline
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transform: "rotate(180deg)",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    right: 36,
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: 24,
                    fontWeight: 900,
                    textShadow: "0 0 10px rgba(0,0,0,0.8)",
                  }}
                >
                  {winPercent}%
                </div>
              </div>
            </div>
          </div>

          {/* Div 3: quay mồi + quay auto */}
          <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
            <div
              style={{
                flex: 1,
                height: 82,
                backgroundImage: "url('/assets/bg-quay.png')",
                backgroundSize: "100% 100%",
                backgroundRepeat: "no-repeat",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 900, lineHeight: 1.2 }}>Quay mồi</div>
              <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>
                {manualValues.rounds} vòng - Mức min {manualValues.minBet}
              </div>
            </div>
            <div
              style={{
                flex: 1,
                height: 82,
                backgroundImage: "url('/assets/bg-quay.png')",
                backgroundSize: "100% 100%",
                backgroundRepeat: "no-repeat",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div style={{ fontSize: 16, fontWeight: 900, lineHeight: 1.2 }}>Quay Auto</div>
              <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>
                {autoValues.rounds} vòng - Mức min {autoValues.minBet}
              </div>
            </div>
          </div>

          {/* Div 4: khung giờ */}
          <div
            style={{
              marginTop: 12,
              height: 52,
              border: "2px solid #d9c900",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              fontFamily: '"Source Code Pro", monospace',
              fontSize: "22.92px",
              fontWeight: 700,
              lineHeight: "100%",
              letterSpacing: "0%",
            }}
          >
            <span>KHUNG GIỜ : </span>
            <span
              style={{
                fontFamily: '"Source Code Pro", monospace',
                fontSize: "18.95px",
                fontWeight: 700,
                lineHeight: "100%",
                letterSpacing: "0%",
                textAlign: "center",
                color: "#FFFFFF",
              }}
            >
              {timeSlotText}
            </span>
          </div>

          {/* Div 5: input điểm */}
          <div style={{ marginTop: 12 }}>
            <input
              type="number"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              placeholder="NHẬP ĐIỂM"
              style={{
                width: "100%",
                height: 52,
                textAlign: "center",
                background: "#3f0000",
                border: "1px solid #a80000",
                color: "#fff",
                fontSize: 24,
                fontWeight: 700,
                outline: "none",
              }}
            />
          </div>

          {/* Div 6: bắt đầu phân tích */}
          <button
            type="button"
            disabled={isSpinning}
            onClick={handleStart}
            style={{
              marginTop: 12,
              width: "100%",
              height: 56,
              border: "none",
              borderRadius: 12,
              background: "linear-gradient(90deg, #ff2f2f 0%, #ff7b00 100%)",
              color: "#fff",
              fontSize: window.innerWidth <= 430 ? 23 : 31,
              fontWeight: 900,
              letterSpacing: 0.5,
              cursor: isSpinning ? "not-allowed" : "pointer",
              opacity: isSpinning ? 0.7 : 1,
            }}
          >
            [BẮT ĐẦU PHÂN TÍCH]
          </button>
        </div>
      </div>

      <ModalConfirmLogout
        isShowLogout={isShowLogout}
        setIsShowLogout={() => {
          setIsShowLogout(false);
        }}
      />
    </div>
  );
};

export default TableGameNew;
